import { useState } from 'react';
import { User, LogOut } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useAccountActions } from '@/hooks/useAccountActions';
import { cn } from '@/lib/utils';

interface TopBarUserEntryProps {
  user?: {
    displayName?: string | null;
    email?: string | null;
    photoURL?: string | null;
  };
  /** First name from user profile - used for avatar initial and display */
  firstName?: string | null;
  /** Full resolved display name from user profile */
  displayName?: string | null;
}

/**
 * TopBarUserEntry - Unified user menu for mobile and desktop
 *
 * Mobile (<640px): Bottom sheet with large buttons
 * Desktop (≥640px): Dropdown menu
 *
 * Features:
 * - View profile action
 * - Logout with confirmation
 * - Safe-area support on mobile
 * - Accessible (ARIA, keyboard navigation)
 */
export function TopBarUserEntry({ user, firstName, displayName }: TopBarUserEntryProps) {
  const { t } = useTranslation('common');
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const { viewProfile, handleSignOut } = useAccountActions();

  // Get user initials for avatar fallback
  // Priority: firstName > displayName > email
  const getInitials = () => {
    // Use firstName if available (from user profile)
    if (firstName && firstName.length > 0) {
      return firstName.charAt(0).toUpperCase();
    }
    // Fallback to displayName (from Firebase Auth)
    if (user?.displayName) {
      return user.displayName
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    // Final fallback to email
    if (user?.email) {
      return user.email[0].toUpperCase();
    }
    return 'U';
  };

  const handleViewProfile = () => {
    setOpen(false);
    viewProfile();
  };

  const handleLogoutClick = () => {
    setOpen(false);
    setShowConfirm(true);
  };

  const handleConfirmLogout = async () => {
    setShowConfirm(false);
    await handleSignOut();
  };

  // Avatar button (shared)
  const avatarButton = (
    <button
      className={cn(
        'rounded-full focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
        'transition-opacity hover:opacity-80'
      )}
      aria-label={t('account.menu')}
    >
      <Avatar className="h-9 w-9">
        <AvatarImage src={user?.photoURL || undefined} alt={user?.displayName || 'User'} />
        <AvatarFallback className="bg-primary text-primary-foreground text-sm font-medium">
          {getInitials()}
        </AvatarFallback>
      </Avatar>
    </button>
  );

  return (
    <>
      {isMobile ? (
        // Mobile: Bottom Sheet
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild aria-haspopup="dialog" aria-expanded={open}>
            {avatarButton}
          </SheetTrigger>
          <SheetContent
            side="bottom"
            className="z-50"
            style={{
              paddingBottom: 'calc(env(safe-area-inset-bottom) + 12px)',
            }}
          >
            <SheetHeader>
              <SheetTitle>{t('account.title')}</SheetTitle>
              <SheetDescription className="sr-only">{t('account.menu')}</SheetDescription>
            </SheetHeader>
            <div className="grid gap-2 pt-4">
              {/* View Profile */}
              <button
                onClick={handleViewProfile}
                className={cn(
                  'flex items-center gap-3 w-full px-4 py-3 rounded-lg',
                  'text-left font-medium',
                  'bg-card hover:bg-accent transition-colors',
                  'focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
                  'min-h-[56px]'
                )}
                role="menuitem"
              >
                <User className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
                <span>{t('account.viewProfile')}</span>
              </button>

              {/* Logout */}
              <button
                onClick={handleLogoutClick}
                className={cn(
                  'flex items-center gap-3 w-full px-4 py-3 rounded-lg',
                  'text-left font-medium',
                  'bg-card hover:bg-destructive/10 text-destructive transition-colors',
                  'focus-visible:ring-2 focus-visible:ring-destructive focus-visible:ring-offset-2',
                  'min-h-[56px]'
                )}
                role="menuitem"
              >
                <LogOut className="h-5 w-5" aria-hidden="true" />
                <span>{t('account.logout')}</span>
              </button>
            </div>
          </SheetContent>
        </Sheet>
      ) : (
        // Desktop: Dropdown Menu
        <DropdownMenu open={open} onOpenChange={setOpen}>
          <DropdownMenuTrigger asChild aria-haspopup="menu" aria-expanded={open}>
            {avatarButton}
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-60 z-50">
            {/* User info */}
            {user && (
              <>
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{displayName || user.displayName || 'User'}</p>
                  <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                </div>
                <DropdownMenuSeparator />
              </>
            )}

            {/* View Profile */}
            <DropdownMenuItem onClick={handleViewProfile} className="cursor-pointer">
              <User className="mr-2 h-4 w-4" aria-hidden="true" />
              <span>{t('account.viewProfile')}</span>
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            {/* Logout */}
            <DropdownMenuItem
              onClick={handleLogoutClick}
              className="cursor-pointer text-destructive focus:text-destructive focus:bg-destructive/10"
            >
              <LogOut className="mr-2 h-4 w-4" aria-hidden="true" />
              <span>{t('account.logout')}</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {/* Logout Confirmation Dialog */}
      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent className="z-50">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('account.logoutConfirm.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('account.logoutConfirm.description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('account.logoutConfirm.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmLogout}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('account.logoutConfirm.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
